import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { STORAGE_KEYS, buildPath } from '../../config';
import { useAuth } from '../../lib/auth';
import { ChatAPI } from '../../lib/api';
import { chatUserEmail, chatUserName } from '../../lib/chatUser';
import {
  createChatSocket,
  inferMessageStatus,
  newClientId,
  statusLabel,
} from '../../lib/chatSocket';
import Loader from '../../components/Loader.jsx';
import ChatUserProfileModal from './ChatUserProfileModal.jsx';

function normalizeChatMessage(raw) {
  const item = { ...raw };
  if (item.body == null && item.message != null) item.body = item.message;
  if (item.sender == null && item.sender_id != null) item.sender = item.sender_id;
  if (item.id == null && item.message_id != null) item.id = item.message_id;
  if (!item.delivery_status && item.status && item.status !== 'error') {
    item.delivery_status = item.status;
  }
  return item;
}

function sortChatMessages(list) {
  return [...list].sort((a, b) => {
    const ta = new Date(a.created_on || 0).getTime();
    const tb = new Date(b.created_on || 0).getTime();
    if (ta !== tb) return ta - tb;
    return (a.id || 0) - (b.id || 0);
  });
}

function unwrapMessageList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

/**
 * ChatPage — single-screen chat surface.
 *
 * Layout is unchanged from the polling UI. Live delivery now uses the
 * vanilla WebSocket client in ``chatSocket.js``. On mount we load
 * history from ``GET /api/chat/messages/`` and only then open the
 * WebSocket (receive-only). Sends go through ``POST /api/chat/messages/``
 * so the row is committed in the tenant schema before live fan-out.
 */
export default function ChatPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const activeId = conversationId ? Number(conversationId) : null;

  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [connState, setConnState] = useState('idle');
  const [connMeta, setConnMeta] = useState({});
  const [profileUser, setProfileUser] = useState(null);

  const messagesEndRef = useRef(null);
  const lastSeenIdRef = useRef(0);
  const socketRef = useRef(null);

  // ----- helpers ----------------------------------------------------------
  const sortConversations = (list) =>
    [...list].sort(
      (a, b) => new Date(b.updated_on || 0) - new Date(a.updated_on || 0),
    );

  const conversationOthers = useCallback(
    (conv) => {
      if (!conv) return [];
      return (conv.participants || [])
        .map((p) => p.user_detail)
        .filter((u) => u && u.id !== (user && user.id));
    },
    [user],
  );

  const conversationPeer = useCallback(
    (conv) => {
      if (!conv || conv.is_group) return null;
      const others = conversationOthers(conv);
      return others.length === 1 ? others[0] : null;
    },
    [conversationOthers],
  );

  const otherParticipantsLabel = useCallback(
    (conv) => {
      if (!conv) return '';
      if (conv.title) return conv.title;
      const others = conversationOthers(conv);
      if (!others.length) return '(empty conversation)';
      return others.map((u) => chatUserName(u) || u.email).join(', ');
    },
    [conversationOthers],
  );

  const openProfile = useCallback((person) => {
    if (!person || person.id == null) return;
    setProfileUser(person);
  }, []);

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId) || null,
    [conversations, activeId],
  );

  const mergeIncoming = useCallback((incoming, { replacePending } = {}) => {
    if (!incoming || !incoming.length) return;
    setMessages((prev) => {
      const byId = new Map();
      const byClient = new Map();
      const next = [...prev];
      for (const m of next) {
        if (m.id != null) byId.set(m.id, m);
        if (m.client_id) byClient.set(m.client_id, m);
      }
      for (const raw of incoming) {
        const item = normalizeChatMessage(raw);
        let idx = -1;
        if (replacePending && item.client_id && byClient.has(item.client_id)) {
          idx = next.findIndex((m) => m.client_id === item.client_id);
        } else if (item.id != null && byId.has(item.id)) {
          idx = next.findIndex((m) => m.id === item.id);
        }
        if (idx >= 0) {
          next[idx] = { ...next[idx], ...item };
        } else {
          next.push(item);
        }
        if (item.id != null) byId.set(item.id, item);
        if (item.client_id) byClient.set(item.client_id, item);
      }
      return sortChatMessages(next);
    });
    const ids = incoming
      .map((m) => m.id || m.message_id)
      .filter((id) => typeof id === 'number');
    if (ids.length) {
      lastSeenIdRef.current = Math.max(lastSeenIdRef.current, ...ids);
    }
  }, []);

  const bumpConversationPreview = useCallback((conversationId, msg) => {
    setConversations((prev) => {
      const next = prev.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              last_message: {
                id: msg.id,
                body: msg.body || msg.message,
                sender_id: msg.sender || msg.sender_id,
                created_on: msg.created_on || msg.timestamp,
              },
              updated_on: msg.created_on || msg.timestamp || c.updated_on,
            }
          : c,
      );
      return sortConversations(next);
    });
  }, []);

  // ----- conversation list ------------------------------------------------
  const loadConversations = useCallback(async () => {
    try {
      const data = await ChatAPI.conversations();
      const list = Array.isArray(data) ? data : data.results || [];
      setConversations(sortConversations(list));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load conversations.');
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // ----- contacts (lazy) --------------------------------------------------
  const ensureContacts = useCallback(async () => {
    if (contacts.length) return;
    try {
      const data = await ChatAPI.contacts();
      setContacts(Array.isArray(data) ? data : data.results || []);
    } catch {
      /* non-fatal */
    }
  }, [contacts.length]);

  const fetchHistory = useCallback(async (conversationId, { since, markRead } = {}) => {
    const data = await ChatAPI.messages(conversationId, { since });
    const list = unwrapMessageList(data).map(normalizeChatMessage);
    if (markRead) {
      ChatAPI.markRead(conversationId).catch(() => {});
    }
    return list;
  }, []);

  const handleSocketEvent = useCallback((evt) => {
    if (!evt || typeof evt !== 'object') return;
    const kind = evt.type;

    if (kind === 'error') {
      const cid = evt.client_id;
      const detail = evt.error || 'Server error.';
      if (cid) {
        setMessages((prev) =>
          prev.map((m) =>
            m.client_id === cid
              ? { ...m, delivery_status: 'error', error: detail }
              : m,
          ),
        );
      }
      setError(detail);
      return;
    }

    if (kind === 'chat.message' || evt.body || evt.message) {
      const mine = user && (evt.sender === user.id || evt.sender_id === user.id);
      mergeIncoming([evt], { replacePending: true });
      bumpConversationPreview(activeId, evt);
      if (!mine && evt.id) {
        socketRef.current?.sendJson({ type: 'delivered', message_id: evt.id });
        ChatAPI.markRead(activeId).catch(() => {});
        socketRef.current?.sendJson({ type: 'read' });
      }
      return;
    }

    if (kind === 'status') {
      if (evt.status === 'delivered' && evt.message_id) {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== evt.message_id) return m;
            if (m.delivery_status === 'read' || m.delivery_status === 'error') return m;
            return { ...m, delivery_status: 'delivered' };
          }),
        );
      }
      if (evt.status === 'read') {
        const readAt = evt.read_at || new Date().toISOString();
        const readerId = evt.reader_id || evt.user_id;
        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== activeId) return c;
            return {
              ...c,
              participants: (c.participants || []).map((p) => {
                const pid = p.user_detail ? p.user_detail.id : p.user;
                if (pid !== readerId) return p;
                return { ...p, last_read_at: readAt };
              }),
            };
          }),
        );
      }
    }
  }, [activeId, bumpConversationPreview, mergeIncoming, user]);

  // ----- messages: REST history first, then websocket --------------------
  useEffect(() => {
    setMessages([]);
    lastSeenIdRef.current = 0;
    setConnState('idle');
    setConnMeta({});
    if (!activeId) return undefined;

    let cancelled = false;
    let sock = null;
    setLoadingMsgs(true);

    const connectSocket = () => {
      sock = createChatSocket({
        roomId: activeId,
        getToken: () => {
          try {
            return localStorage.getItem(STORAGE_KEYS.ACCESS);
          } catch {
            return null;
          }
        },
        onEvent: (evt) => {
          if (!cancelled) handleSocketEvent(evt);
        },
        onConnectionChange: (state, meta) => {
          if (cancelled) return;
          setConnState(state);
          setConnMeta(meta || {});
          if (state === 'auth_failed' && meta?.reason) {
            setError(meta.reason);
          }
          if (state === 'open') {
            queueMicrotask(() => socketRef.current?.sendJson({ type: 'read' }));
          }
        },
        onReconnected: () => {
          if (cancelled) return;
          fetchHistory(activeId, {
            since: lastSeenIdRef.current || undefined,
            markRead: true,
          })
            .then((list) => {
              if (!cancelled) mergeIncoming(list);
            })
            .catch(() => {});
        },
      });
      socketRef.current = sock;
      if (cancelled) {
        sock.close();
        socketRef.current = null;
      }
    };

    const loadThenConnect = async () => {
      try {
        const list = await fetchHistory(activeId, { markRead: true });
        if (cancelled) return;
        const next = sortChatMessages(list);
        setMessages(next);
        const ids = next
          .map((m) => m.id)
          .filter((id) => typeof id === 'number');
        lastSeenIdRef.current = ids.length ? Math.max(...ids) : 0;
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Could not load messages.');
        }
      } finally {
        if (!cancelled) setLoadingMsgs(false);
      }
      if (cancelled) return;
      connectSocket();
    };
    loadThenConnect();

    return () => {
      cancelled = true;
      if (sock) sock.close();
      socketRef.current = null;
    };
  }, [activeId, fetchHistory, handleSocketEvent, mergeIncoming]);

  // Auto-scroll on new messages.
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // ----- actions ----------------------------------------------------------
  const onPickConversation = (id) => {
    setError(null);
    navigate(buildPath.chatConversation(id));
  };

  const onStartDirect = async (other) => {
    setShowContacts(false);
    setError(null);
    try {
      const conv = await ChatAPI.openDirect(other.id);
      setConversations((prev) => {
        const without = prev.filter((c) => c.id !== conv.id);
        return sortConversations([conv, ...without]);
      });
      navigate(buildPath.chatConversation(conv.id));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not start chat.');
    }
  };

  const onSend = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text || !activeId || sending) return;
    setSending(true);
    setError(null);
    const clientId = newClientId();
    const pending = {
      id: null,
      client_id: clientId,
      conversation: activeId,
      sender: user && user.id,
      sender_detail: user
        ? {
            id: user.id,
            email: user.email,
            full_name: user.full_name,
          }
        : null,
      body: text,
      created_on: new Date().toISOString(),
      delivery_status: 'pending',
    };
    setMessages((prev) => [...prev, pending]);
    setBody('');
    try {
      // Persist through HTTP (tenant middleware) so a refresh can reload
      // the row. The WebSocket is receive-only for live delivery.
      const created = await ChatAPI.send(activeId, text);
      mergeIncoming(
        [{ ...created, client_id: clientId, delivery_status: 'sent' }],
        { replacePending: true },
      );
      bumpConversationPreview(activeId, created);
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.client_id === clientId
            ? {
                ...m,
                delivery_status: 'error',
                error:
                  err?.response?.data?.detail
                  || err?.response?.data?.body?.[0]
                  || 'Could not save message.',
              }
            : m,
        ),
      );
      setError(
        err?.response?.data?.detail || 'Could not save message.',
      );
    } finally {
      setSending(false);
    }
  };

  const retryMessage = async (msg) => {
    if (!msg || !activeId) return;
    const text = (msg.body || '').trim();
    if (!text) return;
    const clientId = msg.client_id || newClientId();
    setMessages((prev) =>
      prev.map((m) =>
        (m.client_id && m.client_id === msg.client_id) || m === msg
          ? { ...m, client_id: clientId, delivery_status: 'pending', error: undefined }
          : m,
      ),
    );
    setError(null);
    try {
      const created = await ChatAPI.send(activeId, text);
      mergeIncoming(
        [{ ...created, client_id: clientId, delivery_status: 'sent' }],
        { replacePending: true },
      );
      bumpConversationPreview(activeId, created);
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.client_id === clientId
            ? {
                ...m,
                delivery_status: 'error',
                error: err?.response?.data?.detail || 'Could not save message.',
              }
            : m,
        ),
      );
    }
  };

  const onComposerKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const renderMessage = (m) => {
    const mine = user && m.sender === user.id;
    const sender = m.sender_detail || {};
    const senderName = chatUserName(sender);
    const senderEmail = chatUserEmail(sender);
    const status = mine
      ? inferMessageStatus(m, activeConversation, user && user.id)
      : null;
    const key = m.client_id || m.id;
    return (
      <div
        key={key}
        className={'ty-chat__msg' + (mine ? ' ty-chat__msg--mine' : '')}
      >
        {!mine && senderName && (
          <button
            type="button"
            className="ty-chat__identity ty-chat__identity--msg"
            onClick={() => openProfile({ ...sender, id: sender.id || m.sender })}
            aria-label={`View profile for ${senderName}`}
          >
            <span className="ty-chat__identity-name">{senderName}</span>
            {senderEmail ? (
              <span className="ty-chat__identity-email">{senderEmail}</span>
            ) : null}
          </button>
        )}
        <span className="ty-chat__msg-body">{m.body}</span>
        <span className="ty-chat__msg-meta">
          {m.created_on
            ? new Date(m.created_on).toLocaleTimeString([], {
                hour: '2-digit', minute: '2-digit',
              })
            : ''}
          {mine && status && (
            <button
              type="button"
              className={
                'ty-chat__msg-status ty-chat__msg-status--' + status +
                (status === 'error' ? ' ty-chat__msg-status--retry' : '')
              }
              onClick={status === 'error' ? () => retryMessage(m) : undefined}
              title={status === 'error' ? 'Retry send' : undefined}
            >
              {statusLabel(status, m.error)}
            </button>
          )}
        </span>
      </div>
    );
  };

  // ----- render -----------------------------------------------------------
  const activePeer = conversationPeer(activeConversation);
  const activeMembers = conversationOthers(activeConversation);

  return (
    <div className={`ty-chat ${activeId ? 'ty-chat--has-active' : ''}`}>
      {/* ============= sidebar ============= */}
      <aside className="ty-chat__sidebar">
        <header className="ty-chat__sidebar-header">
          <h2>Conversations</h2>
          <button
            type="button"
            className="ty-btn ty-btn--sm"
            onClick={() => {
              ensureContacts();
              setShowContacts((v) => !v);
            }}
          >+ New</button>
        </header>

        {loadingConvs ? (
          <Loader />
        ) : conversations.length === 0 ? (
          <div className="ty-empty">No conversations yet. Click + New to start one.</div>
        ) : (
          <ul className="ty-chat__list">
            {conversations.map((c) => {
              const last = c.last_message;
              const peer = conversationPeer(c);
              const peerEmail = chatUserEmail(peer);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className={
                      'ty-chat__list-item' +
                      (c.id === activeId ? ' ty-chat__list-item--active' : '')
                    }
                    onClick={() => onPickConversation(c.id)}
                  >
                    <span className="ty-chat__list-name">
                      <span>{otherParticipantsLabel(c)}</span>
                      {c.unread_count > 0 && (
                        <span className="ty-chat__unread">{c.unread_count}</span>
                      )}
                    </span>
                    {peerEmail ? (
                      <span className="ty-chat__list-email">{peerEmail}</span>
                    ) : null}
                    {last && (
                      <span className="ty-chat__list-preview">{last.body}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {showContacts && (
          <div className="ty-chat__contacts" role="listbox">
            {contacts.length === 0 ? (
              <div className="ty-empty">No other users in your organisation.</div>
            ) : (
              contacts.map((c) => {
                const name = chatUserName(c) || c.email || `User ${c.id}`;
                const email = chatUserEmail(c);
                return (
                  <div key={c.id} className="ty-chat__contact">
                    <button
                      type="button"
                      className="ty-chat__identity ty-chat__identity--contact"
                      onClick={() => openProfile(c)}
                      aria-label={`View profile for ${name}`}
                    >
                      <span className="ty-chat__identity-name">{name}</span>
                      {email ? (
                        <span className="ty-chat__identity-email">{email}</span>
                      ) : null}
                    </button>
                    <button
                      type="button"
                      className="ty-btn ty-btn--sm ty-btn--ghost"
                      onClick={() => onStartDirect(c)}
                    >
                      Chat
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </aside>

      {/* ============= panel ============= */}
      <section className="ty-chat__panel">
        {!activeId ? (
          <div className="ty-empty" style={{ margin: 'auto' }}>
            Pick a conversation on the left, or start a new one.
          </div>
        ) : (
          <>
            <header className="ty-chat__panel-header">
              <button
                type="button"
                className="ty-btn ty-btn--ghost ty-btn--sm"
                onClick={() => navigate('/chat')}
                aria-label="Back to conversations"
              >‹</button>
              {activeConversation && activeConversation.is_group ? (
                <>
                  {activeConversation.title ? (
                    <span className="ty-chat__panel-title">
                      {activeConversation.title}
                    </span>
                  ) : null}
                  <span className="ty-chat__panel-members">
                    {activeMembers.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        className="ty-chat__member-chip"
                        onClick={() => openProfile(member)}
                        aria-label={`View profile for ${chatUserName(member) || member.email}`}
                      >
                        <span>{chatUserName(member) || member.email}</span>
                        {chatUserEmail(member) ? (
                          <span className="ty-chat__member-chip-email">
                            {chatUserEmail(member)}
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </span>
                </>
              ) : activePeer ? (
                <button
                  type="button"
                  className="ty-chat__identity ty-chat__identity--header"
                  onClick={() => openProfile(activePeer)}
                  aria-label={`View profile for ${chatUserName(activePeer) || otherParticipantsLabel(activeConversation)}`}
                >
                  <span className="ty-chat__identity-name ty-chat__panel-title">
                    {otherParticipantsLabel(activeConversation)}
                  </span>
                  {chatUserEmail(activePeer) ? (
                    <span className="ty-chat__identity-email">
                      {chatUserEmail(activePeer)}
                    </span>
                  ) : null}
                </button>
              ) : (
                <span className="ty-chat__panel-title">
                  {otherParticipantsLabel(activeConversation)}
                </span>
              )}
              {connState === 'reconnecting' && (
                <span className="ty-chat__reconnect" role="status">Reconnecting…</span>
              )}
              {connState === 'connecting' && (
                <span className="ty-chat__reconnect" role="status">Connecting…</span>
              )}
            </header>

            <div className="ty-chat__messages">
              {loadingMsgs && messages.length === 0 ? (
                <Loader />
              ) : messages.length === 0 ? (
                <div className="ty-empty">No messages yet — say hi 👋</div>
              ) : (
                messages.map(renderMessage)
              )}
              <div ref={messagesEndRef} />
            </div>

            {error && <div className="ty-error" role="alert">{error}</div>}

            <form className="ty-chat__composer" onSubmit={onSend}>
              <textarea
                className="ty-textarea"
                placeholder="Type a message…  (Enter to send, Shift+Enter for newline)"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={onComposerKey}
                rows={1}
              />
              <button
                type="submit"
                className="ty-btn"
                disabled={sending || !body.trim()}
              >
                {sending ? '…' : 'Send'}
              </button>
            </form>
          </>
        )}
      </section>
      {profileUser ? (
        <ChatUserProfileModal
          user={profileUser}
          onClose={() => setProfileUser(null)}
        />
      ) : null}
    </div>
  );
}
